import { ApproveTransactionButton, RejectTransactionButton } from "@atoms";
import { Badge as ShadcnBadge } from "@shadcn";
import clsx from "clsx";

type StatusVariant = "text" | "badge";

export type TransactionListItemProps = {
  memberName: string;
  companyName?: string;
  secondaryLabel?: string;
  submittedDate?: string | Date;
  amount: number;
  statusLabel?: string;
  statusVariant?: StatusVariant;
  receiptUrl?: string;
  onApprove?: () => void;
  onReject?: () => void;
  isApproving?: boolean;
  isRejecting?: boolean;
  className?: string;
  disableApprove?: boolean;
};

const formatAmount = (value: number): string =>
  typeof value === "number"
    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(value);

const formatDate = (value?: string | Date): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getStatusBadgeClasses = (label?: string): string => {
  const normalized = label?.toLowerCase();
  switch (normalized) {
    case "rejected":
      return "border-secondary-medium text-secondary-medium bg-white";
    case "pending":
      return "border-attention-dark text-attention-dark bg-white";
    case "completed":
      return "border-successful-medium text-successful-medium bg-white";
    default:
      return "border-neutral-light text-neutral-darkest bg-white";
  }
};

export const TransactionListItem = ({
  memberName,
  companyName,
  secondaryLabel,
  submittedDate,
  amount,
  statusLabel,
  statusVariant = "text",
  receiptUrl,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
  className = "",
  disableApprove = false,
}: TransactionListItemProps) => {
  const initials =
    memberName
      ?.trim()
      ?.split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "?";

  const formattedDate = formatDate(submittedDate);
  const formattedAmount = formatAmount(amount);
  const showActions = typeof onApprove === "function" || typeof onReject === "function";

  return (
    <div className={clsx("flex flex-wrap items-center gap-4", className)}>
      <div className="flex items-center gap-4 flex-1 min-w-20">
        <div className="w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] flex-shrink-0 rounded-full flex items-center justify-center bg-neutral-medium2 font-bold text-sm text-black">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm text-gray-900 truncate">{memberName || "Unknown User"}</div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 min-w-0">
            {companyName ? <span className="truncate">{companyName}</span> : null}
            {secondaryLabel ? (
              <span className="truncate max-w-[16rem]">{secondaryLabel}</span>
            ) : null}
            {formattedDate ? (
              <>
                {(companyName || secondaryLabel) ? <span>•</span> : null}
                <span>{formattedDate}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="w-20 pr-1 text-right flex-shrink-0">
          <div className="text-sm font-medium text-gray-900">${formattedAmount}</div>
          {receiptUrl ? (
            <button
              type="button"
              className="text-xs font-semibold text-brand-orange hover:text-primary-dark transition-colors mt-1"
              onClick={() => window.open(receiptUrl, "_blank")}
            >
              Receipt
            </button>
          ) : null}
          {statusLabel ? (
            statusVariant === "badge" ? (
              <div className="mt-1 flex justify-end">
                <ShadcnBadge
                  variant="outline"
                  className={clsx(
                    "rounded-full border bg-white px-3 py-1 text-xs font-semibold",
                    getStatusBadgeClasses(statusLabel),
                  )}
                >
                  {statusLabel}
                </ShadcnBadge>
              </div>
            ) : (
              <div className="text-xs font-semibold text-neutral-mediumDark mt-1">{statusLabel}</div>
            )
          ) : null}
        </div>
        {showActions ? (
          <div className="flex items-center gap-2 flex-shrink-0">
            {onApprove ? (
              <ApproveTransactionButton
                onClick={onApprove}
                isLoading={!!isApproving}
                disabled={!!isApproving || !!isRejecting || disableApprove}
              />
            ) : null}
            {onReject ? (
              <RejectTransactionButton
                onClick={onReject}
                isLoading={!!isRejecting}
                disabled={!!isApproving || !!isRejecting}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
